import { BasicStorageItem, Credential, ServiceProvider, StorageItem } from '@uploadcam/common';
import { RefreshCredentialInteractor } from '../../../auth/domain/interactors/refresh-credential.interactor';
import {
  StorageAdapter,
  StorageCapabilities,
  FeedItemsImageUrls,
  FeedItemUrlMap,
  StorageItemId,
} from './adapter';
import { StorageGetNextListPage, StorageListPage } from './list-page';

export class StorageManager implements StorageGetNextListPage {
  private refreshCredentialPromise?: Promise<void>;

  constructor(
    private readonly adapter: StorageAdapter,
    private credential: Credential,
    private readonly refreshCredential: RefreshCredentialInteractor,
  ) {
    adapter.setManager(this);
  }

  private async checkCredentials(): Promise<void> {
    // Refresh tokens if needed. Concurrent callers (e.g. parallel uploads)
    // must share a single refresh request.
    if (!this.credential.isExpired) {
      return;
    }

    this.refreshCredentialPromise ??= this.refreshCredential
      .execute(this.credential)
      .then((credential) => {
        this.credential = credential;
        this.adapter.updateAccessToken(credential.accessToken);
      })
      .finally(() => {
        this.refreshCredentialPromise = undefined;
      });

    return this.refreshCredentialPromise;
  }

  public async write(parent: StorageItem, file: File): Promise<StorageItem> {
    await this.checkCredentials();
    return this.adapter.write(parent, file);
  }

  public async createFolder(parent: StorageItem, name: string): Promise<StorageItem> {
    await this.checkCredentials();
    return this.adapter.createFolder(parent, name);
  }

  public async renameFolder(folder: StorageItem, name: string): Promise<StorageItem> {
    await this.checkCredentials();
    return this.adapter.renameFolder(folder, name);
  }

  public async listFolder(
    item: StorageItem,
    onlyFolders: boolean = false,
  ): Promise<StorageListPage> {
    await this.checkCredentials();
    return this.adapter.listFolder(item, onlyFolders);
  }

  public async moveItems(
    targetFolder: StorageItem,
    items: StorageItem[],
  ): Promise<Record<StorageItemId, boolean>> {
    await this.checkCredentials();
    return this.adapter.moveItems(targetFolder, items);
  }

  public async trashItems(items: StorageItem[]): Promise<Record<StorageItemId, boolean>> {
    await this.checkCredentials();
    return this.adapter.trashItems(items);
  }

  public async getDownloadUrls(items: BasicStorageItem[]): Promise<FeedItemUrlMap> {
    await this.checkCredentials();
    return this.adapter.getDownloadUrls(items);
  }

  public async getFileUrl(file: StorageItem): Promise<string> {
    await this.checkCredentials();
    return this.adapter.getFileUrl(file);
  }

  public async getFolderUrl(folder: StorageItem): Promise<string> {
    await this.checkCredentials();
    return this.adapter.getFolderUrl(folder);
  }

  public getProviderUrl(): string {
    return this.adapter.getProviderUrl();
  }

  public async getNextFolderPage(onlyFolders: boolean, options: unknown): Promise<StorageListPage> {
    await this.checkCredentials();
    return this.adapter.getNextFolderPage(onlyFolders, options);
  }

  public getProvider(): ServiceProvider {
    return this.adapter.getProvider();
  }

  public getRootFolder(): Promise<StorageItem> {
    return this.adapter.getRootFolder();
  }

  // TODO: Remove? This might not be needed with the new `StorageImage` service/component system
  public async getItemsImageUrls(items: readonly BasicStorageItem[]): Promise<FeedItemsImageUrls> {
    await this.checkCredentials();

    const [thumbnails, previews] = await Promise.all([
      this.adapter.getThumbnailUrls(items),
      this.adapter.getPreviewUrls(items),
    ]);

    return { thumbnails, previews };
  }

  public async getItemsPreviewUrls(items: readonly BasicStorageItem[]): Promise<FeedItemUrlMap> {
    await this.checkCredentials();
    return this.adapter.getPreviewUrls(items);
  }

  public async getItemsThumbnailUrls(items: readonly BasicStorageItem[]): Promise<FeedItemUrlMap> {
    await this.checkCredentials();
    return this.adapter.getThumbnailUrls(items);
  }

  public getCapabilities(): StorageCapabilities {
    return this.adapter.getCapabilities();
  }

  public async setCaption(item: Readonly<StorageItem>, caption?: string): Promise<void> {
    await this.checkCredentials();
    return this.adapter.setCaption(item, caption);
  }
}
